import { useState } from "react";
import { format } from "date-fns";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  Button,
  Avatar,
  Pill,
} from "@/design/components";
import { useSeed, useMutate, useActorId, usePeopleIndex, useMoney } from "@/domain/hooks";
import { addSpendComment } from "@/domain/mutations";
import type { SpendRequest } from "@/domain/types";

/**
 * The Reporter's side of the query loop (spec §6.3). A contractor lead can query an ask
 * "with a comment, sends back to Reporter for more info" — this drawer is where the
 * Reporter reads that query and replies. The reply is a `comment` on the shared discussion
 * thread, so it surfaces live in the approver's `SpendAskDrawer`, who then re-decides.
 *
 * Demo/seed only: reads and writes the seed store. The ask row passed in is a snapshot, so
 * we re-read the live ask from the store by id (same reason as `SpendAskDrawer`) — without
 * it a freshly sent reply would not appear until the drawer was reopened.
 */
export function QueryReplyDrawer({
  ask,
  open,
  onOpenChange,
}: {
  ask: SpendRequest | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const seed = useSeed();
  const mutate = useMutate();
  const actorId = useActorId();
  const people = usePeopleIndex();
  const money = useMoney();
  const [reply, setReply] = useState("");

  if (!ask) return null;
  const live = seed.spendRequests.find((r) => r.id === ask.id) ?? ask;
  const project = seed.projects.find((p) => p.id === live.projectId);
  const milestone = seed.milestones.find((m) => m.id === live.milestoneId);
  const thread = live.thread ?? [];

  function sendReply() {
    if (!reply.trim()) return;
    mutate((s) => addSpendComment(s, live.id, actorId, reply.trim()));
    setReply("");
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="520px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{live.title}</DrawerTitle>
        <p className="mt-1 flex items-center gap-2 text-sm text-mute">
          <span className="text-lg font-semibold tabular-nums text-ink">{money(live.amount)}</span>
          <Pill variant="warn">Queried</Pill>
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-faint">
          {project && <span>{project.name}</span>}
          {milestone && <span>· {milestone.name}</span>}
        </p>
      </DrawerHeader>

      <DrawerBody className="space-y-5">
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Why it was sent back</h4>
          <p className="rounded-md bg-warnSoft px-3 py-2 text-sm text-warnStrong">
            {live.reviewerComment ?? "Your contractor lead asked for more information before approving."}
          </p>
        </section>

        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Discussion</h4>
          {thread.length === 0 ? (
            <p className="text-sm text-faint">No queries or comments yet.</p>
          ) : (
            <ul className="space-y-2">
              {thread.map((c) => (
                <li key={c.id} className="flex items-start gap-2">
                  <Avatar initials={people[c.authorId]?.avatarInitials ?? "?"} size={26} />
                  <div className="min-w-0 flex-1 rounded-md border border-line bg-card px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-ink">{people[c.authorId]?.fullName ?? c.authorId}</span>
                      <span className="text-[11px] text-faint">{c.kind === "query" ? "Query" : "Comment"} · {format(new Date(c.at), "d MMM")}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-mute">{c.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </DrawerBody>

      <DrawerFooter className="flex-col items-stretch gap-2">
        {/* text-base (16px), not text-sm: iOS Safari auto-zooms the whole
            viewport on focusing any text input under 16px, a well-known
            mobile bug - every other text field in the reporter flows
            (RaiseAsk, SubmitEvidence) already uses text-base for exactly
            this reason; this hand-rolled textarea was the one place it was
            missed. size="lg" on the buttons below for the same reason
            Button's own default (md, 36px) is under the 44px touch-target
            minimum - "Send reply" is this drawer's entire purpose. */}
        <textarea
          autoFocus
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply to your contractor lead…"
          className="h-24 w-full rounded-md border border-line p-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" size="lg" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>Close</Button>
          <Button size="lg" className="w-full sm:w-auto" onClick={sendReply} disabled={!reply.trim()}>Send reply</Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
}
